import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

/**
 * Hook to check if user has unredeemed partner codes available
 * Returns pending redemptions that match active partner codes
 */
export const usePartnerCodeRedemptionStatus = (partnerCode?: string) => {
  const { userId } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['partner-code-redemption-status', userId, partnerCode],
    queryFn: async () => {
      if (!userId || !partnerCode) return null;
      
      // Check if code exists and is active
      const { data: codeData, error: codeError } = await supabase
        .from('partner_codes')
        .select('id, partner_name, credits_value, discount_type, discount_percentage, is_active')
        .eq('code', partnerCode)
        .eq('is_active', true)
        .maybeSingle();
      
      if (codeError || !codeData) {
        console.log('Partner code not found or inactive:', partnerCode);
        return null;
      }
      
      // Check if user has already redeemed this code
      const { data: redemptionData, error: redemptionError } = await supabase
        .from('partner_code_redemptions')
        .select('id, redeemed_at')
        .eq('user_id', userId)
        .eq('partner_code_id', codeData.id)
        .maybeSingle();
      
      if (redemptionError) {
        console.error('Error checking redemption status:', redemptionError);
        return null;
      }
      
      const hasRedeemed = !!redemptionData;
      
      return {
        codeInfo: codeData,
        hasRedeemed,
        canRedeem: !hasRedeemed && codeData.is_active,
        redemptionDate: redemptionData?.redeemed_at,
      };
    },
    enabled: !!userId && !!partnerCode,
  });

  return {
    status: data,
    isLoading,
  };
};
